import { useState, useEffect, useRef, useMemo, Children } from 'react';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  ListItemButton,
  Button,
  Card as MuiCard,
  CardContent,
  Stack,
  Avatar as MuiAvatar,
  SvgIcon,
  LinearProgress,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faClock,
  faPlane,
  faBriefcase,
  faChartLine,
  faWallet,
  faFileAlt,
  faChartBar,
  faCog,
  faBell,
  faSearch,
  faChevronRight,
  faRobot,
  faPaperPlane,
  faTimes,
  faPlus,
  faCheck,
  faBuilding,
  faAward,
  faSignOutAlt,
  faBook,
  faShieldAlt,
  faSparkles,
  faArrowRight,
  faArrowLeft,
  faDownload,
  faUser,
  faStar,
  faMapPin,
  faEnvelope,
  faPhone,
  faPencil,
  faAlertTriangle,
  faHashtag,
  faCalendar,
  faGraduationCap,
  faUserPlus,
  faComment,
  faBars,
  faCamera,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  Wallet,
  Users,
  ShieldCheck,
  Plus,
  Check,
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  GraduationCap,
  Clock,
  X,
  CalendarOff,
  CircleUser,
  Sparkles,
  TrendingUp,
  TrendingDown,
  LayoutDashboard,
  FileText,
  BarChart3,
  Home,
  BookOpen,
  UserPlus,
  AlertTriangle,
  Settings,
  Search,
  Bell,
  Send,
  Hash,
  Calendar,
  Pencil,
  CornerDownLeft,
  ChevronRight,
  MessageSquare,
  LogOut,
  Bot,
  Download,
  Star,
  PanelLeft,
  UserCog,
  SlidersHorizontal,
  Award,
  Menu as MenuIcon,
} from 'lucide-react';
import { api } from './api';
import EmployeesDataGrid from './EmployeesDataGrid';
import AppDataGrid, {
  DataGridCard,
  DataGridPage,
  PersonCell,
  TextCell,
  GridStatusPill,
  useAttendanceColumns,
  useHistoryColumns,
} from './AppDataGrid';
import { useAuth } from './auth';
import { useAppSettings } from './SettingsContext';
import { buildPath, parsePath } from './routing';
import {
  StatusPill,
  UserAvatar,
  Header,
  SectionHeader,
  GridLayout,
  EmptyState,
} from './components';
import { FONT, AG, PIE_COLORS, STATUS_COLOR, statusTone } from './theme/tokens';
import { initials, empEmail, tenure, fmtPKR, fmtM, greeting } from './lib/format';
import { IMAGE_ACCEPT, MAX_IMAGE_BYTES, downscaleSquareDataUrl } from './lib/image';
import { STAGES, JOB_STAGES, jobTone, jobName } from './lib/ats';
import { printPayDoc, docRow, printReport } from './lib/print';
import { OKR_RATINGS, RATING_NUM, ratingTone, okrStatusTone, okrTabBtn, okrLbl, weightedProgress, avgRatingLabel, okrsByEmployee } from './lib/okr';

const PILL = {
  success: { bg: 'rgba(16,185,129,.12)', c: AG.successDark, d: AG.success },
  warning: { bg: 'rgba(247,144,9,.12)', c: AG.warningDark, d: AG.warning },
  error: { bg: 'rgba(240,68,56,.12)', c: AG.errorDark, d: AG.error },
  info: { bg: 'rgba(6,174,212,.12)', c: AG.infoDark, d: AG.info },
  primary: { bg: 'rgba(var(--primary-rgb),.12)', c: AG.primaryDark, d: AG.primary },
  neutral: { bg: 'rgba(108,115,127,.12)', c: AG.n600, d: AG.n500 },
};
function Pill({ children, tone = 'neutral', dot }) {
  const s = PILL[tone] || PILL.neutral;
  return (
    <span className="ag-severity-pill" style={{ background: s.bg, color: s.c }}>
      {dot && (
        <span
          style={{ width: 6, height: 6, borderRadius: 99, background: s.d }}
        />
      )}
      {children}
    </span>
  );
}
function Avatar({ name, size = 'md', onDark, account, src }) {
  if (account) {
    const px = size === 'sm' ? 32 : 40;
    const fs = px === 32 ? 14 : 18;
    return (
      <div className="ag-account-avatar-wrap" style={{ width: px, height: px }}>
        <div
          className="ag-account-avatar"
          style={{ width: px, height: px, fontSize: fs, overflow: 'hidden' }}
        >
          {src ? (
            <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            initials(name)
          )}
        </div>
        <span className="ag-account-avatar-status" aria-hidden />
      </div>
    );
  }
  const px = size === 'sm' ? 32 : size === 'lg' ? 48 : size === 'xl' ? 80 : 40;
  const fs = size === 'xl' ? 26 : size === 'lg' ? 17 : size === 'sm' ? 12 : 13;
  return (
    <div
      style={{
        width: px,
        height: px,
        borderRadius: 99,
        background: onDark ? '#FFFFFF' : AG.n200,
        color: AG.n900,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        fontSize: fs,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initials(name)
      )}
    </div>
  );
}

// Click-to-upload profile picture: the avatar itself is the target (click / Enter / Space opens the
// file picker). A camera badge signals it's editable; a remove affordance appears only when a custom
// photo is set. Validates type + size and downscales client-side, then calls `onSave(dataUrl|null)`
// (the caller persists it — self via /auth/me, HR via /employees/:id — and refreshes its own state).
// Generic so both the account dropdown and the HR employee-detail header can reuse it.
function AvatarEditor({ name, avatar, onSave, onError, size = 56 }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const shown = preview ?? avatar ?? undefined;

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the user re-pick the same file after an error
    if (!file) return;
    if (!IMAGE_ACCEPT.includes(file.type)) return onError?.('Use a JPEG, PNG, or WebP image.');
    if (file.size > MAX_IMAGE_BYTES) return onError?.('Image must be 5 MB or smaller.');
    setBusy(true);
    try {
      const dataUrl = await downscaleSquareDataUrl(file, 256);
      setPreview(dataUrl); // optimistic
      await onSave(dataUrl);
    } catch (err) {
      setPreview(null);
      onError?.(err?.message || 'Could not update the picture. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(e) {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onSave(null);
      setPreview(null);
    } catch (err) {
      onError?.(err?.message || 'Could not remove the picture. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const openPicker = () => { if (!busy) fileRef.current?.click(); };
  const badge = Math.max(16, Math.round(size * 0.32));

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={avatar ? 'Change profile photo' : 'Add a profile photo'}
      onClick={openPicker}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
      style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '50%',
        cursor: busy ? 'default' : 'pointer',
      }}
    >
      <input ref={fileRef} type="file" accept={IMAGE_ACCEPT.join(',')} hidden onChange={onPick} tabIndex={-1} aria-hidden="true" />
      <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', border: `1px solid ${AG.n200}` }}>
        {shown ? (
          <img src={shown} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: AG.n200, color: AG.n900, fontWeight: 600, fontSize: size / 2.7 }}>
            {initials(name)}
          </div>
        )}
      </div>

      {/* Camera badge — the always-visible "editable" cue. */}
      <div
        style={{
          position: 'absolute', right: -2, bottom: -2, width: badge, height: badge, borderRadius: '50%',
          background: AG.primary, color: '#fff', display: 'grid', placeItems: 'center',
          border: '2px solid #fff', boxShadow: '0 1px 3px rgba(16,24,40,0.2)', pointerEvents: 'none',
        }}
      >
        <FontAwesomeIcon icon={faCamera} style={{ fontSize: badge * 0.45 }} />
      </div>

      {/* Remove — only when a custom photo is set. */}
      {avatar && !busy && (
        <button
          type="button"
          aria-label="Remove profile photo"
          onClick={onRemove}
          style={{
            position: 'absolute', right: -6, top: -6, width: badge - 2, height: badge - 2, borderRadius: '50%',
            background: '#fff', color: AG.textSec, display: 'grid', placeItems: 'center', cursor: 'pointer',
            border: `1px solid ${AG.n200}`, boxShadow: '0 1px 3px rgba(16,24,40,0.2)', padding: 0,
          }}
        >
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: (badge - 2) * 0.45 }} />
        </button>
      )}

      {busy && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', background: 'rgba(0,0,0,0.45)' }}>
          <CircularProgress size={size / 3} sx={{ color: '#fff' }} />
        </div>
      )}
    </div>
  );
}

function AccountButton({ user, isHR, logout, onSettings }) {
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [mfaOpen, setMfaOpen] = useState(false);
  const ref = useRef(null);
  const email = user?.email || empEmail(user?.name || '');
  const roleLabel = isHR ? 'Admin' : user?.role || 'Employee';

  // Email the logged-in user a password-reset link (same SES flow as forgot-password).
  const changePassword = async () => {
    if (pwBusy) return;
    setPwBusy(true);
    setErr('');
    setPwMsg('');
    try {
      await api.requestPasswordReset();
      setPwMsg(`We've emailed a reset link to ${email}. It expires in 30 minutes.`);
    } catch (e) {
      setErr(e?.message || 'Could not send the reset link. Please try again.');
    } finally {
      setPwBusy(false);
    }
  };

  // Close on outside click / Escape — plain CSS dropdown (no portal) so it
  // positions correctly under the body's CSS zoom.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Clear any transient messages when the menu closes so they don't linger on reopen.
  useEffect(() => { if (!open) { setErr(''); setPwMsg(''); } }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="ag-account-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-label="Account menu"
      >
        <Avatar name={user?.name} account src={user?.avatar} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 320,
            maxWidth: '90vw',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(16,24,40,0.16), 0 0 0 1px rgba(16,24,40,0.05)',
            zIndex: 1300,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
            <AvatarEditor
              name={user?.name}
              avatar={user?.avatar}
              onSave={async (dataUrl) => {
                const { user: updated } = await api.updateProfile({ avatar: dataUrl });
                setUser(updated);
                setErr('');
              }}
              onError={(m) => setErr(m)}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: AG.text }}>{user?.name}</span>
                <Chip label={roleLabel} size="small" color="primary" variant="outlined" />
              </div>
              <div style={{ fontSize: 13, color: AG.textSec, marginTop: 2, wordBreak: 'break-all' }}>{email}</div>
            </div>
          </div>
          {err && (
            <div style={{ padding: '0 16px 8px', fontSize: 12, color: AG.error }}>{err}</div>
          )}
          {pwMsg && (
            <div style={{ padding: '0 16px 8px', fontSize: 12, color: AG.success || '#12B76A' }}>{pwMsg}</div>
          )}
          <Divider />
          <div style={{ padding: '4px 8px' }}>
            <button
              type="button"
              onClick={changePassword}
              disabled={pwBusy}
              className="navlift"
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, background: 'transparent', color: AG.text, fontSize: 14, fontWeight: 500,
                cursor: pwBusy ? 'default' : 'pointer', textAlign: 'left',
              }}
            >
              <FontAwesomeIcon icon={faKey} style={{ fontSize: 13, color: AG.textSec, width: 16 }} />
              {pwBusy ? 'Sending reset link…' : 'Change password'}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setMfaOpen(true); }}
              className="navlift"
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px',
                borderRadius: 8, background: 'transparent', color: AG.text, fontSize: 14, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 13, color: AG.textSec, width: 16 }} />
                Two-factor authentication
              </span>
              <Chip label={user?.mfaEnabled ? 'On' : 'Off'} size="small" color={user?.mfaEnabled ? 'success' : 'default'} variant="outlined" />
            </button>
          </div>
          <Divider />
          <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
            <Button color="inherit" size="small" onClick={() => { setOpen(false); logout(); }}>Logout</Button>
          </div>
        </div>
      )}
      {mfaOpen && <MfaDialog user={user} setUser={setUser} onClose={() => setMfaOpen(false)} />}
    </div>
  );
}

// Two-factor (TOTP) enrolment / management. Backend issues an otpauth secret; the
// user adds it to an authenticator app and confirms a 6-digit code.
function MfaDialog({ user, setUser, onClose }) {
  const enabled = !!user?.mfaEnabled;
  const [step, setStep] = useState('intro'); // intro | enrol
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const begin = async () => {
    setBusy(true); setErr('');
    try { const r = await api.mfaEnroll(); setSecret(r.secret); setStep('enrol'); }
    catch (e) { setErr(e?.message || 'Could not start setup'); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setErr('');
    try { await api.mfaVerify(code.trim()); setUser({ ...user, mfaEnabled: true }); onClose(); }
    catch { setErr('That code didn’t match — try the current one.'); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setErr('');
    try { await api.mfaDisable(code.trim()); setUser({ ...user, mfaEnabled: false }); onClose(); }
    catch { setErr('That code didn’t match — try the current one.'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Two-factor authentication</DialogTitle>
      <DialogContent>
        {enabled ? (
          <div style={{ paddingTop: 4 }}>
            <p style={{ fontSize: 13, color: AG.textSec, marginBottom: 12 }}>Two-factor authentication is <b style={{ color: AG.success || '#12B76A' }}>on</b>. To turn it off, enter a current 6-digit code from your authenticator app.</p>
            <TextField fullWidth size="small" label="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric' }} />
          </div>
        ) : step === 'intro' ? (
          <p style={{ fontSize: 13, color: AG.textSec, paddingTop: 4 }}>Add a second layer of security. You’ll need an authenticator app such as Google Authenticator, Microsoft Authenticator or Authy.</p>
        ) : (
          <div style={{ paddingTop: 4 }}>
            <p style={{ fontSize: 13, color: AG.textSec, marginBottom: 8 }}>1. In your authenticator app, add an account using this secret key:</p>
            <div style={{ fontFamily: 'monospace', fontSize: 15, letterSpacing: 1, background: AG.n50, border: `1px solid ${AG.n200}`, borderRadius: 8, padding: '10px 12px', wordBreak: 'break-all', marginBottom: 12 }}>{secret}</div>
            <p style={{ fontSize: 13, color: AG.textSec, marginBottom: 8 }}>2. Enter the 6-digit code it shows:</p>
            <TextField fullWidth size="small" label="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric' }} />
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: AG.error, marginTop: 8 }}>{err}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        {enabled ? (
          <Button color="error" variant="contained" onClick={disable} disabled={busy || code.length < 6}>Disable</Button>
        ) : step === 'intro' ? (
          <Button variant="contained" onClick={begin} disabled={busy}>Set up</Button>
        ) : (
          <Button variant="contained" onClick={verify} disabled={busy || code.length < 6}>Verify & enable</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Card({ children, className = '', hover, onClick, style }) {
  return (
    <div
      onClick={onClick}
      className={`ag-card ${hover ? 'ag-card-h' : ''} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

const WIDGET_AVATAR_COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f79009',
  error: '#f04438',
  info: '#06AED4',
  purple: '#9E77ED',
  neutral: '#a4a8ad',
};

const LUCIDE_TO_FA = {
  [Users]: faUsers,
  [Clock]: faClock,
  [CalendarOff]: faPlane,
  [Briefcase]: faBriefcase,
  [Check]: faCheck,
  [X]: faTimes,
  [Wallet]: faWallet,
  [ArrowRight]: faArrowRight,
  [Building2]: faBuilding,
  [ShieldCheck]: faShieldAlt,
  [FileText]: faFileAlt,
  [TrendingUp]: faChartLine,
  [CircleUser]: faUser,
  [Sparkles]: faStar,
};

function DashboardWidget({
  title,
  amount,
  faIcon,
  avatarColor = WIDGET_AVATAR_COLORS.primary,
  className = '',
  sx,
}) {
  return (
    <MuiCard className={className} sx={{ width: '100%', ...sx }}>
      <Stack
        alignItems="center"
        direction="row"
        height="88px"
        p={2}
        spacing={2}
      >
        <MuiAvatar
          sx={{
            bgcolor: avatarColor,
            color: '#fff',
            width: 48,
            height: 48,
            flexShrink: 0,
          }}
        >
          <SvgIcon sx={{ fontSize: 20 }}>
            <FontAwesomeIcon icon={faIcon || faChartBar} />
          </SvgIcon>
        </MuiAvatar>
        <Stack spacing={0} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            color="text.secondary"
            variant="overline"
            sx={{ lineHeight: 1.5, fontSize: 14 }}
          >
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {amount}
          </Typography>
        </Stack>
      </Stack>
    </MuiCard>
  );
}

function WidgetGrid({ children }) {
  const items = Children.toArray(children);
  const count = items.length;
  const itemSize =
    count === 3 ? { xs: 12, sm: 6, lg: 4 } : { xs: 12, sm: 6, lg: 3 };

  return (
    <Grid
      container
      spacing={2}
      className="page-shrink"
      sx={{ width: '100%', m: 0 }}
    >
      {items.map((child, i) => (
        <Grid key={i} {...itemSize} sx={{ minWidth: 0, display: 'flex' }}>
          <Box sx={{ width: '100%' }}>{child}</Box>
        </Grid>
      ))}
    </Grid>
  );
}

const KPI_TONES = {
  primary: AG.primary,
  success: AG.success,
  warning: AG.warning,
  error: AG.error,
  info: AG.info,
  purple: AG.purple,
  neutral: AG.n500,
};

function KPI({ label, value, icon: Icon, iconTone = 'primary', delay, hint }) {
  const c = KPI_TONES[iconTone] || KPI_TONES.primary;
  return (
    <Box
      className="fade-up"
      sx={{
        animationDelay: delay != null ? `${delay}ms` : undefined,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        bgcolor: '#fff',
        border: `1px solid ${AG.n200}`,
        borderRadius: '18px',
        p: 2.5,
        transition: 'transform .22s cubic-bezier(.22,1,.36,1), box-shadow .22s, border-color .22s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 18px 38px rgba(16,24,40,0.10)',
          borderColor: `${c}55`,
        },
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: AG.text, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 13, color: AG.textSec, mt: 0.75, fontWeight: 500 }}>
            {label}
          </Typography>
          {hint && (
            <Typography sx={{ fontSize: 12, color: c, mt: 0.5, fontWeight: 600 }}>{hint}</Typography>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            width: 46,
            height: 46,
            borderRadius: '13px',
            display: 'grid',
            placeItems: 'center',
            background: `linear-gradient(135deg, ${c} 0%, ${c}C8 100%)`,
            color: '#fff',
            boxShadow: `0 10px 20px ${c}3D`,
          }}
        >
          {Icon && <Icon size={22} strokeWidth={2.2} />}
        </Box>
      </Box>
    </Box>
  );
}

function CardSectionHeader({ title, sub, action }) {
  if (!title && !action) return null;
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        pt={3}
        pb={sub ? 1 : 1.5}
        gap={2}
        flexWrap="wrap"
      >
        <Stack spacing={0.25}>
          {title && (
            <Typography variant="h6" color="text.primary">
              {title}
            </Typography>
          )}
          {sub && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              {sub}
            </Typography>
          )}
        </Stack>
        {action}
      </Stack>
      <Divider />
    </>
  );
}

function ChartCard({
  title,
  sub,
  action,
  children,
  className = '',
  sx,
  contentSx,
}) {
  return (
    <MuiCard
      className={className}
      sx={{ height: '100%', width: '100%', ...sx }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <CardSectionHeader title={title} sub={sub} action={action} />
        <Box sx={{ px: 3, py: 1.5, ...contentSx }}>{children}</Box>
      </CardContent>
    </MuiCard>
  );
}

function SectionTitle({ title, sub, action }) {
  return (
    <div className="page-shrink flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h2
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: AG.text,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </h2>
        {sub && (
          <p style={{ fontSize: 14, color: AG.textSec, marginTop: 2 }}>{sub}</p>
        )}
      </div>
      {action}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: 12,
        fontSize: 12,
        boxShadow: '0 3px 14px rgba(0,0,0,0.08)',
        border: `1px solid ${AG.n200}`,
      }}
    >
      <p style={{ fontWeight: 600, color: AG.text, marginBottom: 6 }}>
        {label}
      </p>
      {payload.map((p, i) => (
        <div
          key={i}
          className="flex items-center gap-2"
          style={{ marginTop: i ? 3 : 0 }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: p.color || p.fill,
            }}
          />
          <span style={{ color: AG.textSec, textTransform: 'capitalize' }}>
            {p.name}
          </span>
          <span style={{ fontWeight: 600, color: AG.text, marginLeft: 'auto' }}>
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

const PrimaryBtn = ({ icon: Icon, children, onClick, disabled, style, type }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={style}
    type={type}
    className="ag-btn ag-btn-primary flex items-center gap-2"
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);
const OutlineBtn = ({ icon: Icon, children, onClick, disabled, style, type }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={style}
    type={type}
    className="ag-btn ag-btn-outline flex items-center gap-2"
  >
    {Icon && <Icon size={16} />}
    {children}
  </button>
);

function CenterSpinner({ label = 'Loading…', size = 44, pad = '64px 0' }) {
  const ring = Math.round(size / 11); // ~9% of diameter → crisp thin ring
  return (
    <div className="ag-loader" style={{ padding: pad }} role="status" aria-live="polite">
      <span
        className="ag-loader-ring"
        style={{ width: size, height: size, '--ring': `${ring}px` }}
      />
      {label && <span className="ag-loader-label">{label}</span>}
    </div>
  );
}

const DRAWER_TABLE_SX = {
  width: '100%',
  tableLayout: 'fixed',
  '& .MuiTableCell-body:first-of-type': {
    width: '38%',
    maxWidth: 226,
    color: 'text.secondary',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-body:last-of-type': {
    width: '62%',
  },
};

function DrawerFormRow({ label, req, children }) {
  return (
    <TableRow>
      <TableCell>
        {label}
        {req && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
            *
          </Box>
        )}
      </TableCell>
      <TableCell>{children}</TableCell>
    </TableRow>
  );
}

// Section header inside the employee drawer (spans both columns).
function DrawerSection({ title }) {
  return (
    <TableRow>
      <TableCell colSpan={2} sx={{ border: 0, pt: 2.5, pb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

// Anchor select menus directly below the field (not centered over it, which is
// MUI's default and reads as a floating overlay in the middle of the dialog).
const DRAWER_SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

// `native` renders a browser-native <select> (children must be <option>). Use it
// inside zoomed/scrolled dialogs: the page-level `zoom: 0.8` desyncs MUI's
// portaled menu from its anchor, but the browser positions a native dropdown
// correctly under the field regardless of zoom.
function DrawerTextField({ className, select, native, SelectProps, ...props }) {
  const resolvedSelectProps = select
    ? native
      ? { native: true, ...SelectProps }
      : { MenuProps: DRAWER_SELECT_MENU_PROPS, ...SelectProps }
    : SelectProps;
  return (
    <TextField
      fullWidth
      variant="filled"
      hiddenLabel
      select={select}
      SelectProps={resolvedSelectProps}
      className={['drawer-field', className].filter(Boolean).join(' ')}
      {...props}
    />
  );
}

// Add / edit an employee. One component, `mode` = "add" | "edit".
// Right-side MUI drawer (FFApp CustomDrawer style); header + footer pinned, body scrolls.
function PageScroll({ children }) {
  return <div className="page-scroll">{children}</div>;
}

const PTh = ({ children, style }) => (
  <th style={{ textAlign: 'left', textTransform: 'uppercase', fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: AG.n500, padding: '13px 16px', background: AG.n50, borderBottom: `1px solid ${AG.divider}`, whiteSpace: 'nowrap', ...style }}>{children}</th>
);
const PTd = ({ children, style }) => (
  <td style={{ padding: '12px 16px', fontSize: 14, color: AG.n600, borderBottom: `1px solid ${AG.divider}`, ...style }}>{children}</td>
);
const PerfTable = ({ head, children, note }) => (
  <Card style={{ overflow: 'hidden' }}>
    {note && (
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${AG.divider}`, fontSize: 13, color: AG.n500 }}>{note}</div>
    )}
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{head.map((h, i) => <PTh key={i}>{h}</PTh>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </Card>
);


export { Pill, Avatar, AvatarEditor, AccountButton, Card, DashboardWidget, WidgetGrid, KPI, CardSectionHeader, ChartCard, SectionTitle, ChartTip, CenterSpinner, PrimaryBtn, OutlineBtn, PageScroll, PTh, PTd, PerfTable, DrawerFormRow, DrawerSection, DrawerTextField, DRAWER_TABLE_SX };
